import * as React from "react";
import { InlineImageBlock, InlineMetaPreviewBlock, InlineVideoBlock } from "./ChatInlineMediaBlocks";
import { PlainMarkdownTable, ProductTableCard } from "./ChatMarkdownTables";
import { renderInline } from "./chatInlineFormatting";
import {
  isImageUrl,
  isProductTable,
  isVideoUrl,
  parseMarkdownTable,
  preprocessChatText,
} from "./chatMarkdownUtils";

const META_PREVIEW_IFRAME_REGEX =
  /<iframe[^>]*\bsrc="(https:\/\/business\.facebook\.com\/ads\/api\/preview_iframe\.php\?[^"]+)"[^>]*><\/iframe>/i;

export function renderMarkdown(
  text: string,
  options?: { compact?: boolean; mediaAutoPreview?: boolean; skipMediaUrls?: string[] },
) {
  if (!text) return null;
  const compact = options?.compact ?? false;
  const mediaAutoPreview = options?.mediaAutoPreview ?? false;
  const skipUrls = options?.skipMediaUrls?.length
    ? new Set(options.skipMediaUrls)
    : undefined;
  const lines = preprocessChatText(text, skipUrls).split("\n");
  const elements: React.ReactNode[] = [];
  let listItems: { content: string; ordered: boolean; num?: string }[] = [];
  let listKey = 0;

  function flushList() {
    if (listItems.length === 0) return;
    const isOrdered = listItems[0].ordered;
    const Tag = isOrdered ? "ol" : "ul";
    elements.push(
      <Tag
        key={`list-${listKey++}`}
        className={`${isOrdered ? "list-decimal" : "list-disc"} pl-4 my-1 space-y-0.5 text-[inherit]`}
      >
        {listItems.map((item, j) => (
          <li key={j} className="text-[inherit]">
            {renderInline(item.content, `li-${listKey}-${j}`)}
          </li>
        ))}
      </Tag>,
    );
    listItems = [];
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    const parsedTable = parseMarkdownTable(lines, i);
    if (parsedTable) {
      flushList();
      const isProduct = isProductTable(parsedTable.table.headers);
      elements.push(
        isProduct ? (
          <ProductTableCard
            key={`table-${i}`}
            headers={parsedTable.table.headers}
            rows={parsedTable.table.rows}
          />
        ) : (
          <PlainMarkdownTable
            key={`table-${i}`}
            headers={parsedTable.table.headers}
            rows={parsedTable.table.rows}
            mediaAutoPreview={mediaAutoPreview}
            compact={compact}
          />
        ),
      );
      i = parsedTable.nextIndex - 1;
      continue;
    }

    const metaPreviewMatch = line.match(META_PREVIEW_IFRAME_REGEX);
    if (metaPreviewMatch) {
      flushList();
      const previewUrl = metaPreviewMatch[1];
      let caption: string | undefined;
      const prevEl = elements[elements.length - 1];
      const prevLine = i > 0 ? lines[i - 1].trim() : "";
      if (prevLine && !prevLine.match(/^#{1,3}\s/)) {
        caption = prevLine.replace(/^\*\*|\*\*$/g, "").trim();
        if (
          React.isValidElement(prevEl) &&
          typeof prevEl.key === "string" &&
          prevEl.key.startsWith("br-")
        ) {
          elements.pop();
        }
        const newTail = elements[elements.length - 1];
        if (
          React.isValidElement(newTail) &&
          typeof newTail.key === "string" &&
          newTail.key.startsWith("l-")
        ) {
          elements.pop();
        }
      }
      elements.push(
        <InlineMetaPreviewBlock
          key={`meta-preview-${i}`}
          url={previewUrl}
          caption={caption}
          compact={compact}
        />,
      );
      continue;
    }

    const imgMatch = line.match(/^\s*!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (imgMatch) {
      flushList();
      const alt = imgMatch[1].trim();
      const url = imgMatch[2].trim();
      elements.push(
        <InlineImageBlock key={`img-${i}`} url={url} alt={alt} compact={compact} />,
      );
      continue;
    }

    if (mediaAutoPreview) {
      const urlMatch = line.match(/^\s*(https?:\/\/\S+)\s*$/);
      if (urlMatch) {
        const url = urlMatch[1];
        if (isImageUrl(url)) {
          flushList();
          elements.push(<InlineImageBlock key={`url-img-${i}`} url={url} compact={compact} />);
          continue;
        }
        if (isVideoUrl(url)) {
          flushList();
          elements.push(<InlineVideoBlock key={`url-vid-${i}`} url={url} compact={compact} />);
          continue;
        }
      }

      const labelledMatch = line.match(/^\s*(?:[-*]\s+|\d+\.\s+)?([^\n:]{1,80}?):\s+(https?:\/\/\S+)\s*$/);
      if (labelledMatch) {
        const label = labelledMatch[1].trim();
        const url = labelledMatch[2];
        if (isImageUrl(url)) {
          flushList();
          elements.push(
            <InlineImageBlock key={`url-img-${i}`} url={url} alt={label} compact={compact} />,
          );
          continue;
        }
        if (isVideoUrl(url)) {
          flushList();
          elements.push(
            <InlineVideoBlock key={`url-vid-${i}`} url={url} alt={label} compact={compact} />,
          );
          continue;
        }
      }
    }

    const headerMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const cls =
        level === 1
          ? "ds-body-strong mt-2 mb-1"
          : level === 2
            ? "ds-body-sm-strong mt-1.5 mb-0.5"
            : "text-[13px] font-semibold mt-1 mb-0.5";
      elements.push(
        <div key={`h-${i}`} className={cls}>
          {renderInline(headerMatch[2], `h-${i}`)}
        </div>,
      );
      continue;
    }

    const ulMatch = line.match(/^[\s]*[-*]\s+(.+)$/);
    if (ulMatch) {
      if (listItems.length > 0 && listItems[0].ordered) flushList();
      listItems.push({ content: ulMatch[1], ordered: false });
      continue;
    }

    const olMatch = line.match(/^[\s]*(\d+)\.\s+(.+)$/);
    if (olMatch) {
      if (listItems.length > 0 && !listItems[0].ordered) flushList();
      listItems.push({ content: olMatch[2], ordered: true, num: olMatch[1] });
      continue;
    }

    flushList();

    if (line.trim() === "") {
      elements.push(<div key={`br-${i}`} className="h-1.5" />);
      continue;
    }

    elements.push(
      <span key={`l-${i}`}>
        {i > 0 && lines[i - 1].trim() !== "" && !lines[i - 1].match(/^#{1,3}\s/) && <br />}
        {renderInline(line, `l-${i}`)}
      </span>,
    );
  }
  flushList();
  return <>{elements}</>;
}
